// src/utils/tawk.js

let loadingPromise = null;
let loaded = false;

const TAWK_SRC = "https://embed.tawk.to/696e4291270e04197b207a46/1jfbb830a";
const SCRIPT_ID = "tawk-script";

/**
 * Loads the Tawk widget script exactly once.
 * - Resolves true if widget is ready; false if timed out/failed.
 * - If the load fails, it clears the internal promise so subsequent calls can retry.
 */
export function bootTawk({ timeoutMs = 8000 } = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(false);
  }

  if (loaded) return Promise.resolve(true);

  // Already injected?
  if (document.getElementById(SCRIPT_ID)) {
    return loadingPromise || Promise.resolve(true);
  }

  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve) => {
    let settled = false;

    const finalize = (ok) => {
      if (settled) return;
      settled = true;

      loaded = ok === true;

      // If not ok, allow future retries
      if (!loaded) {
        loadingPromise = null;
      }

      resolve(loaded);
    };

    // Create expected globals
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // When Tawk signals it's fully loaded
    window.Tawk_API.onLoad = function () {
      finalize(true);
    };

    const s1 = document.createElement("script");
    s1.id = SCRIPT_ID;
    s1.async = true;
    s1.src = TAWK_SRC;
    s1.charset = "UTF-8";
    s1.setAttribute("crossorigin", "*");

    // If script fails to load, allow retry
    s1.onerror = () => {
      finalize(false);
    };

    document.head.appendChild(s1);

    // Timeout fallback — don’t block UX forever; allow retry later
    window.setTimeout(() => {
      finalize(false);
    }, timeoutMs);
  });

  return loadingPromise;
}

/**
 * Quietly show the chat bubble (recommended for trial onboarding).
 * - Boots if needed, then shows widget without opening the chat window.
 */
export async function showTawkBubble() {
  const ok = await bootTawk();
  if (!ok) return false;

  try {
    window.Tawk_API?.showWidget?.();
    // Intentionally NOT calling maximize()
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Explicit open (user asked for help).
 * - Boots if needed, then opens the chat window.
 */
export async function openTawkChat() {
  const ok = await bootTawk();
  if (!ok) return false;

  try {
    window.Tawk_API?.showWidget?.();
    window.Tawk_API?.maximize?.();
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Hide the chat widget (if loaded).
 */
export function hideTawk() {
  try {
    window.Tawk_API?.hideWidget?.();
  } catch (_) {}
}

/**
 * Optional: attach user context (email/name/org) once loaded.
 * Safe no-op if API isn’t ready.
 */
export function identifyTawk(attributes = {}) {
  try {
    window.Tawk_API?.setAttributes?.(attributes, () => {});
  } catch (_) {}
}
